/*
 * Copyright 2024 The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { createElement, forwardRef } from 'react';
import { BoxProps } from './types';
import { useDefinition } from '../../hooks/useDefinition';
import { BoxDefinition } from './definition';

/**
 * A general-purpose layout primitive that can render as any HTML element and supports spacing, sizing, and background props.
 *
 * @public
 */
export const Box = forwardRef<HTMLDivElement, BoxProps>((props, ref) => {
  const { ownProps, restProps, dataAttributes, utilityStyle } = useDefinition(
    BoxDefinition,
    props,
  );
  const { classes, as, childrenWithBgProvider } = ownProps;

  return createElement(
    as,
    {
      ref,
      className: classes.root,
      style: { ...utilityStyle, ...ownProps.style },
      ...dataAttributes,
      ...restProps,
    },
    childrenWithBgProvider,
  );
});

Box.displayName = 'Box';
